import { useAppDownload } from '@/context/AppDownloadContext';

const platforms = [
  {
    icon: 'ri-android-fill',
    name: 'Android',
    description: 'Google Play Store',
    status: 'Disponible',
    color: 'emerald',
  },
  {
    icon: 'ri-apple-fill',
    name: 'iPhone',
    description: 'App Store',
    status: 'Próximamente',
    color: 'slate',
  },
  {
    icon: 'ri-android-line',
    name: 'Android TV',
    description: 'Google Play TV',
    status: 'Próximamente',
    color: 'emerald',
  },
  {
    icon: 'ri-amazon-fill',
    name: 'Fire TV',
    description: 'Amazon Appstore',
    status: 'Próximamente',
    color: 'orange',
  },
  {
    icon: 'ri-smartphone-line',
    name: 'Samsung TV',
    description: 'Tizen Store',
    status: 'Próximamente',
    color: 'sky',
  },
  {
    icon: 'ri-tv-2-line',
    name: 'LG TV',
    description: 'webOS Store',
    status: 'Próximamente',
    color: 'rose',
  },
];

const platformColorMap: Record<string, { bg: string; border: string; iconBg: string; iconColor: string; badgeBg: string; badgeColor: string }> = {
  emerald: { bg: 'from-emerald-500/8 to-emerald-500/3', border: 'border-emerald-500/15 hover:border-emerald-500/30', iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-600', badgeBg: 'bg-emerald-500/10', badgeColor: 'text-emerald-700' },
  slate:   { bg: 'from-slate-500/8 to-slate-500/3', border: 'border-slate-500/15 hover:border-slate-500/30', iconBg: 'bg-slate-500/10', iconColor: 'text-slate-600', badgeBg: 'bg-slate-500/10', badgeColor: 'text-slate-700' },
  orange:  { bg: 'from-orange-500/8 to-orange-500/3', border: 'border-orange-500/15 hover:border-orange-500/30', iconBg: 'bg-orange-500/10', iconColor: 'text-orange-600', badgeBg: 'bg-orange-500/10', badgeColor: 'text-orange-700' },
  sky:     { bg: 'from-sky-500/8 to-sky-500/3', border: 'border-sky-500/15 hover:border-sky-500/30', iconBg: 'bg-sky-500/10', iconColor: 'text-sky-600', badgeBg: 'bg-sky-500/10', badgeColor: 'text-sky-700' },
  rose:    { bg: 'from-rose-500/8 to-rose-500/3', border: 'border-rose-500/15 hover:border-rose-500/30', iconBg: 'bg-rose-500/10', iconColor: 'text-rose-600', badgeBg: 'bg-rose-500/10', badgeColor: 'text-rose-700' },
};

export default function AppsSection() {
  const { openModal } = useAppDownload();

  return (
    <section id="apps" className="relative py-20 md:py-28 overflow-hidden bg-background-100">
      {/* Ambient glow */}
      <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-accent-500/3 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-500/3 rounded-full blur-3xl pointer-events-none"></div>

      {/* Dot grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
        backgroundSize: '20px 20px',
      }}></div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* ── Header ── */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100/70 border border-accent-200/70 text-accent-700 text-xs font-semibold mb-5">
            <i className="ri-smartphone-line text-xs"></i>
            Ecosistema RFW
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-[1.1]">
            Tu radio en{' '}
            <span className="text-accent-600">todos lados</span>
          </h2>
          <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            RFW está donde está tu audiencia. Android, iPhone, Smart TVs y más. Una sola plataforma, todas las pantallas.
          </p>
        </div>

        {/* ── Platform grid ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-10">
          {platforms.map((platform) => {
            const c = platformColorMap[platform.color];
            return (
              <div
                key={platform.name}
                className={`group relative rounded-2xl border ${c.border} bg-gradient-to-br ${c.bg} p-5 md:p-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl`}
              >
                {/* Icon */}
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${c.iconBg} ${c.iconColor} mb-3 transition-all duration-300 group-hover:scale-110`}>
                  <i className={`${platform.icon} text-xl`}></i>
                </div>

                {/* Name */}
                <h4 className="text-foreground-950 font-bold text-sm mb-1">{platform.name}</h4>

                {/* Description */}
                <p className="text-foreground-400 text-[10px] leading-relaxed mb-3">{platform.description}</p>

                {/* Status badge */}
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full ${c.badgeBg} ${c.badgeColor} text-[10px] font-semibold whitespace-nowrap`}>
                  {platform.status === 'Disponible' ? (
                    <i className="ri-check-line text-[9px]"></i>
                  ) : (
                    <i className="ri-time-line text-[9px]"></i>
                  )}
                  {platform.status}
                </span>
              </div>
            );
          })}
        </div>

        {/* ── CTA Button ── */}
        <div className="flex justify-center">
          <button
            onClick={openModal}
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 text-sm md:text-base font-bold cursor-pointer transition-all duration-300 hover:gap-4 hover:shadow-lg hover:shadow-accent-500/20 whitespace-nowrap"
          >
            <i className="ri-download-2-line text-lg transition-transform duration-300 group-hover:-translate-y-0.5"></i>
            <span>Descargar App</span>
            <i className="ri-arrow-right-line text-base transition-transform duration-300 group-hover:translate-x-0.5"></i>
          </button>
        </div>

        <p className="mt-4 text-center text-foreground-400 text-[11px]">
          Disponible en todas las tiendas. Elegí tu dispositivo y empezá a escuchar.
        </p>
      </div>
    </section>
  );
}